/**
 * Bootstrap del checkout-widget.
 *
 * El fragment se lee y se borra ANTES del primer render y una sola vez por carga:
 * hacerlo dentro de un componente lo expondria al doble montaje de StrictMode, que
 * en el segundo pase encontraria el hash ya limpio y perderia el client_secret.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import { readRuntimeConfig, resolveApiBaseUrl } from './config/runtimeConfig';
import { rememberOrRecallSession } from './session/hostedSession';
import { parseFragment, parseSessionQuery } from './session/parseFragment';
import './styles.css';

const ROOT_ELEMENT_ID = 'apipay-checkout-root';

const fragment = parseFragment();
const query = parseSessionQuery();
const path = window.location.pathname;
// La del AMBIENTE manda sobre la horneada: Vite inlina `import.meta.env` en el bundle, asi que sin
// esto la imagen publicada llama para siempre a la API con la que se construyo. Ver runtimeConfig.
const apiBaseUrl = resolveApiBaseUrl(readRuntimeConfig(), import.meta.env.VITE_API_BASE_URL);

const container = document.getElementById(ROOT_ELEMENT_ID);
if (container === null) {
  throw new Error(`checkout-widget: falta el contenedor #${ROOT_ELEMENT_ID} en index.html`);
}

// Modo pagina vs embebido: se resuelve una sola vez aqui, junto al resto del bootstrap.
const isTopLevel = window.self === window.top;

// En modo pagina el checkout debe sobrevivir a la ida y vuelta a la pasarela: se vuelve con el token
// de la pasarela y nada mas, asi que la sesion se recuerda al entrar y se recupera al volver.
// Embebido no aplica: el SDK lo reinyecta en la URL del iframe.
//
// Se recuerdan las DOS piezas que App exige, no solo el client_secret: tambien la clave publicable.
// Recordar una sola dejaba el retorno roto —el comprador pagaba y el segundo confirm no se disparaba
// nunca— porque `pk` solo se leia del querystring y la pasarela no lo devuelve.
const recalled = isTopLevel
  ? rememberOrRecallSession(fragment, query.publicKey)
  : { fragment, publicKey: query.publicKey };

createRoot(container).render(
  <StrictMode>
    <App
      path={path}
      fragment={recalled.fragment}
      query={{ ...query, publicKey: recalled.publicKey }}
      apiBaseUrl={apiBaseUrl}
      isTopLevel={isTopLevel}
    />
  </StrictMode>,
);
